import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtEUR } from "../lib/format";
import { PageHeader, StatusBadge } from "../components/Layout";
import { INVOICE_STATUS } from "../lib/format";
import { Euro, Clock, CheckCircle2, TrendingUp, Users, FileText } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

// Couleurs alignées sur les badges de statut (lib/format.js)
const STATUS_COLORS = {
  "À transmettre": "#3B82F6", // bleu
  "Envoyée": "#F59E0B",       // amber
  "Payée": "#10B981",         // emerald
  "En retard": "#F43F5E",     // rose
};
const STATUS_FALLBACK = "#0F172A";

const StatCard = ({ title, value, icon: Icon, hint, testid }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
        <p className="mt-2 font-mono text-2xl font-bold text-slate-900">{value}</p>
        {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
      </div>
      <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#C5A059]" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: api.getDashboard });

  if (isLoading || !data) {
    return (
      <div>
        <PageHeader title="Tableau de bord" subtitle="Aperçu de l'activité MB TRANSPORT" />
        <div className="grid gap-4 animate-pulse">
          <div className="h-28 bg-slate-100 rounded-2xl" />
          <div className="h-64 bg-slate-100 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Tableau de bord" subtitle="Aperçu de l'activité MB TRANSPORT" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-revenue-month" title="CA du mois" value={fmtEUR(data.revenue_month)} icon={Euro} hint={`${fmtEUR(data.revenue_paid)} encaissés`} />
        <StatCard testid="stat-pending-invoices" title="Factures en attente" value={data.pending_count} icon={Clock} hint={fmtEUR(data.pending_amount)} />
        <StatCard testid="stat-paid-invoices" title="Factures payées" value={data.paid_count} icon={CheckCircle2} hint={`${data.invoices_count} au total`} />
        <StatCard testid="stat-avg-invoice" title="Panier moyen / facture" value={fmtEUR(data.avg_invoice)} icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 mb-4">Chiffre d'affaires (6 mois)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.trend} margin={{ left: -10, right: 10 }}>
              <defs>
                <linearGradient id="ca" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#C5A059" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#C5A059" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => fmtEUR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              <Area type="monotone" dataKey="ca" stroke="#C5A059" strokeWidth={2.5} fill="url(#ca)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 mb-4">Répartition par statut</h3>
          {data.invoices_by_status?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.invoices_by_status} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                  {data.invoices_by_status.map((entry, i) => <Cell key={i} fill={STATUS_COLORS[entry.name] || STATUS_FALLBACK} />)}
                </Pie>
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400 py-16 text-center">Aucune facture</p>
          )}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-400" />
          <h3 className="font-display font-semibold text-slate-800">Dernières factures</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="px-5 py-3 font-semibold">N°</th>
                <th className="px-5 py-3 font-semibold">Client</th>
                <th className="px-5 py-3 font-semibold text-right">Montant TTC</th>
                <th className="px-5 py-3 font-semibold">Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_invoices?.length ? data.recent_invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono text-slate-700">{inv.number}</td>
                  <td className="px-5 py-3">{inv.client?.name}</td>
                  <td className="px-5 py-3 text-right font-mono font-semibold">{fmtEUR(inv.total_ttc)}</td>
                  <td className="px-5 py-3"><StatusBadge meta={INVOICE_STATUS[inv.status]} /></td>
                </tr>
              )) : (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400">Aucune facture</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
